import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/services/api'
import { getAuth, setAuth, clearAuth, type AuthInfo } from '../utils/authStorage'

export enum UserRole {
  ADMIN = 'admin',
  CLIENT = 'client',
  TEST = 'test'
}

export const useAuthStore = defineStore('auth', () => {
  const storedAuth = getAuth()
  const authInfo = ref<AuthInfo | null>(storedAuth)
  const isAuthenticated = ref<boolean>(!!storedAuth)

  // Role checks
  const isAdmin = computed(() => authInfo.value?.user.role === UserRole.ADMIN)
  const isClient = computed(() => authInfo.value?.user.role === UserRole.CLIENT)
  const isTest = computed(() => authInfo.value?.user.role === UserRole.TEST)

  async function login(username: string, password: string, recaptchaToken: string) {
    try {
      const response = await api.post('/api/user/login', { username, password, recaptchaToken })
      const loginData: AuthInfo = { ...response.data, loginAt: Date.now() };

      // Update store state
      authInfo.value = loginData
      isAuthenticated.value = true

      // Save to localStorage
      setAuth(loginData)

      return { success: true, data: loginData }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Giriş yapılırken hata oluştu'
      return { success: false, error: errorMessage }
    }
  }

  async function validateToken() {
    try {
      const storedAuth = getAuth()
      if (!storedAuth) {
        logout()
        return false
      }

      // Check if forInvestToken has expired
      if (storedAuth.forInvestToken?.expiresIn && storedAuth.loginAt) {
        const elapsedSeconds = (Date.now() - storedAuth.loginAt) / 1000;
        if (elapsedSeconds >= storedAuth.forInvestToken.expiresIn) {
          console.warn('ForInvest token expired, logging out');
          logout()
          return false
        }
      }

      // Update store with stored auth info
      authInfo.value = storedAuth
      isAuthenticated.value = true

      return true
    } catch (error) {
      logout()
      return false
    }
  }

  async function logout() {
    const token = authInfo.value?.token || getAuth()?.token
    authInfo.value = null
    isAuthenticated.value = false
    clearAuth()
    // Notify registered cleanup handlers (e.g. WebSocket close)
    logoutCallbacks.forEach(cb => { try { cb() } catch (_) {} })
    logoutCallbacks.length = 0

    if (token) {
      try {
        await api.post('/api/user/logout', undefined, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        })
      } catch {
        console.warn('Oturum kapatıldı ancak lisans cache temizliği doğrulanamadı.')
      }
    }
  }

  const logoutCallbacks: Array<() => void> = []

  function onLogout(cb: () => void) {
    logoutCallbacks.push(cb)
  }

  // Get user list
  async function getUsers() {
    try {
      const response = await api.get('/api/users')
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Update user
  async function updateUser(userId: string, userData: any) {
    try {
      const response = await api.put(`/api/users/${userId}`, userData)
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Delete user
  async function deleteUser(userId: string) {
    try {
      const response = await api.delete(`/api/users/${userId}`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Regenerate API keys
  async function regenerateKeys(userId: string) {
    try {
      const response = await api.post(`/api/users/${userId}/regenerate-keys`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Get own profile information
  async function getMyProfile() {
    const token = authInfo.value?.token
    const response = await api.get('/api/user/profile')
    const profile = response.data.user
    const currentAuth = authInfo.value

    if (
      currentAuth &&
      currentAuth.token === token &&
      currentAuth.user.id === profile.id &&
      profile.licensePackage
    ) {
      currentAuth.user.licensePackage = profile.licensePackage
      setAuth(currentAuth)
    }

    return profile
  }

  // Change own password
  async function changePassword(currentPassword: string, newPassword: string) {
    try {
      const response = await api.post('/api/user/change-password', {
        currentPassword,
        newPassword
      })
      return response.data
    } catch (error) {
      throw error
    }
  }


  // Admin change user password (without old password)
  async function changeUserPassword(userId: string, newPassword: string) {
    try {
      const response = await api.post(`/api/users/${userId}/admin-change-password`, {
        newPassword
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Get user chat conversations
  async function getUserChats(userId: string) {
    try {
      const response = await api.get(`/api/users/${userId}/chats`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Add tokens to user (admin only)
  async function addTokensToUser(userId: string, amount: number, description?: string) {
    try {
      const response = await api.post(`/api/users/${userId}/add-tokens`, {
        amount,
        description
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  return {
    authInfo,
    isAuthenticated,
    isAdmin,
    isClient,
    isTest,
    login,
    logout,
    onLogout,
    validateToken,
    getUsers,
    updateUser,
    deleteUser,
    regenerateKeys,
    getMyProfile,
    changePassword,
    changeUserPassword,
    getUserChats,
    addTokensToUser
  }
})
